import React, { useEffect, useState, useMemo } from "react";
import { Col, Row, Spin, Select, Card } from "antd";
import BUWiseCountGraph from "../utils/BUWiseCountGraph";
import UtilStackGraph from "../utils/UtilStackGraph";
import BillablePieGraph from "../utils/BillablePieGraph";
import NotBillableColumnGraph from "../utils/NotBillableColumnGraph";
import TeamWiseColumnGraph from "../utils/TeamWiseColumnGraph";
import EmployeeMovementLineGraph from "../utils/EmployeeMovementLineGraph";
import { dashboard } from "../api";
import { employeeId } from "../utils/userDetails";
import moment from "moment";
import AttritionRateColumn from "../utils/AttritionrateColumn";

function Dashboard() {
  const [buWiseCount, setBuWiseCount] = useState([]);
  const [utilsByBUCount, setUtilsByBUCount] = useState([]);
  const [unBillableAndAvailableCount, setUnBillableAndAvailableCount] =
    useState([]);
  const [nonBillCount, setNonBillCount] = useState([]);
  const [teamWiseExperience, setTeamWiseExperience] = useState([]);
  const [tdEmployeeMovement, setTDEmployeeMovement] = useState([]);
  const [attritionRate, setAttritionRate] = useState([]);

  const [loading, setLoading] = useState(true);
  const [nonBillLoading, setNonBillLoading] = useState(false);
  const [attritionRateLoading, setAttritionRateLoading] = useState(false);

  const [selectTeam, setSelectTeam] = useState("");
  const [buId, setBuId] = useState("");
  const [xPreviousMonth, setXPreviousMonth] = useState(1);
  const [timePeriod, setTimePeriod] = useState("Q");

  const BUWiseCountMemo = useMemo(() => {
    const BUWiseCount = async () => {
      try {
        setLoading(true);
        const response = await dashboard.getBUWiseCount(employeeId);
        setBuWiseCount(response.data.data);
        setBuId(response.data.BUId);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching BU wise count:", error);
      }
    };

    BUWiseCount();
    return null;
  }, [selectTeam]);

  const unBillableAndAvailableMemo = useMemo(() => {
    const unBillableAndAvailableCount = async () => {
      try {
        const response = await dashboard.unBillableAndAvailableCount(
          employeeId,
          selectTeam
        );
        setUnBillableAndAvailableCount(response.data);
      } catch (error) {
        console.error(
          "Error fetching non-billable and available count:",
          error
        );
      }
    };

    unBillableAndAvailableCount();
    return null;
  }, [selectTeam]);

  const nonBillableMemo = useMemo(() => {
    const fetchNonBillableCount = async () => {
      try {
        setNonBillLoading(true);
        const response = await dashboard.billableAndNonBillableCount(
          employeeId,
          xPreviousMonth,
          selectTeam
        );
        setNonBillCount(response.data);
        setNonBillLoading(false);
      } catch (error) {
        setNonBillLoading(false);
        console.error("Error fetching billable and non-billable count:", error);
      }
    };

    fetchNonBillableCount();
    return null;
  }, [xPreviousMonth, selectTeam]);

  const utilizationByBUTeamMemo = useMemo(() => {
    const utilizationByBUTeam = async () => {
      try {
        const response = await dashboard.utilizationByBUTeam(
          employeeId,
          selectTeam
        );
        setUtilsByBUCount(response.data);
      } catch (error) {
        console.error("Error fetching utilization by team count:", error);
      }
    };

    utilizationByBUTeam();
    return null;
  }, [selectTeam]);

  const teamWiseExperienceMemo = useMemo(() => {
    const teamWiseExperienceCount = async () => {
      try {
        const response = await dashboard.teamWiseExperience(
          employeeId,
          selectTeam
        );
        setTeamWiseExperience(response.data);
      } catch (error) {
        console.error("Error fetching team wise experience count:", error);
      }
    };

    teamWiseExperienceCount();
    return null;
  }, [selectTeam]);

  const tdEmployeeMovementMemo = useMemo(() => {
    const fetchTDEmployeeMovement = async () => {
      try {
        const response = await dashboard.TDEmployeeMovement(
          employeeId,
          selectTeam
        );
        setTDEmployeeMovement(response.data);
      } catch (error) {
        console.error("Error fetching TD employee movement:", error);
      }
    };

    fetchTDEmployeeMovement();
    return null;
  }, [selectTeam]);

  const attritionRateMemo = useMemo(() => {
    const fetchAttritionRateCount = async () => {
      try {
        setAttritionRateLoading(true);
        const response = await dashboard.attritionRate(
          employeeId,
          timePeriod,
          selectTeam
        );
        setAttritionRate(response.data);
        setAttritionRateLoading(false);
      } catch (error) {
        setAttritionRateLoading(false);
        console.error("Error fetching attrition rate percentage:", error);
      }
    };

    fetchAttritionRateCount();
    return null;
  }, [timePeriod, selectTeam]);


  const options = [
    { value: "SPAN-BLR-144", label: "ANIL KUMAR V" },
    { value: "SPAN-BLR-0001", label: "BHAGIRATH CHOUDHARY" },
    { value: "SPAN-BLR-1451", label: "PALLAV MAHESHWARI" },
    { value: "SPAN-BLR-2457", label: "RAJESH DOMMARAJU" },
    { value: "SPAN-BLR-191", label: "RAVI REDDY" },
    { value: "SPAN-BLR-033", label: "UMESH BALMRAM" },
  ];

  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  const filteredOptions =
    buId == "SPAN-BLR-0001" || employeeId == "SPAN-BLR-0001"
      ? options
      : options.filter((option) => option.value === buId);

  const handleSelectTeam = (value) => {
    setSelectTeam(value);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        gap: "15px",
        flexDirection: "column",
        margin: "0px 10px 10px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "40px",
          padding: "0px 14px",
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            fontSize: "24px",
            fontWeight: "700",
          }}
        >
          Dashboard
        </span>
        {buId == "SPAN-BLR-0001" || employeeId == "SPAN-BLR-0001" ? (
          <Select
            style={{ width: "140px" }}
            filterOption={filterOption}
            optionFilterProp="children"
            placeholder="Select BU"
            options={filteredOptions}
            value={selectTeam || buId}
            onChange={(value) => handleSelectTeam(value)}
          />
        ) : (
          ""
        )}
      </div>

      <Row gutter={12} style={{ width: "100%" }}>
        <Col span={24}>
          <BUWiseCountGraph
            buWiseCount={buWiseCount}
            selectTeam={selectTeam || buId}
          />
          {BUWiseCountMemo}
        </Col>
      </Row>

      <Row gutter={12} style={{ width: "100%" }}>
        <Col span={8}>
          <UtilStackGraph
            utilsByBUCount={utilsByBUCount}
            buId={buId}
            selectTeam={selectTeam}
          />
          {utilizationByBUTeamMemo}
        </Col>
        <Col span={8}>
          <TeamWiseColumnGraph
            teamWiseExperience={teamWiseExperience}
            buId={buId}
            selectTeam={selectTeam}
          />
          {teamWiseExperienceMemo}
        </Col>
        <Col span={8}>
          <EmployeeMovementLineGraph tdEmployeeMovement={tdEmployeeMovement} />
          {tdEmployeeMovementMemo}
        </Col>
      </Row>

      <Row gutter={12} style={{ width: "100%" }}>
        <Col span={9}>
          <BillablePieGraph dataset={unBillableAndAvailableCount} />
          {unBillableAndAvailableMemo}
        </Col>
        <Col span={7}>
          <NotBillableColumnGraph
            nonBillCount={nonBillCount}
            xPreviousMonth={xPreviousMonth}
            setXPreviousMonth={setXPreviousMonth}
            loading={nonBillLoading}
          />
          {nonBillableMemo}
        </Col>
        <Col span={8}>
          <AttritionRateColumn
            attritionRate={attritionRate}
            timePeriod={timePeriod}
            setTimePeriod={setTimePeriod}
            loading={attritionRateLoading}
          />
          {attritionRateMemo}
        </Col>
      </Row>
    </div>
  );
}

export default Dashboard;
